import React, { useEffect, useMemo, useState } from "react";
import { Checkbox, FormControlLabel, Switch } from "@material-ui/core";
import { DialogWrapMedium } from "../../../controls/components/dialogWrap/DialogWrap";
import {
    InputOrderAdminTitle,
    UserFormServiceButtonClose,
    UserFormServiceButtonCreated,
} from "../../MyBooking/styled/DesStyled";
import { ErrorNotiLogin, FomrmLoginInput } from "../../LoginUser/styled/LoginStyles";
import useAlertDialog from "../../../hooks/useAlertDialog";
import { AlertType } from "../../../hooks/common/useAttachAlertDialog";
import {
    PutBulkPartnerAdmin,
    uploadAvatarMutiImage,
} from "../../../../services/PartnerService";
import { AdminFilterDesControl } from "../hook/FilterDescription";
import {
    formatMoneyInputDisplay,
    parseMoneyToNumber,
    sanitizeMoneyInput,
} from "./moneyInput";
import {
    type ClosureDateRangeRow,
    buildClosureExceptionConfigForApi,
} from "../utils/partnerClosureConfigHelpers";

function validateMoneyValue(value: string, isOptional: boolean = true) {
    if (value === "") return isOptional ? null : "Required";
    if (value.endsWith(".")) return null;
    const num = parseMoneyToNumber(value);
    if (num === null) return "Invalid amount";
    return null;
}

function isCompleteMoneyValue(value: string, isOptional: boolean = true) {
    if (value === "") return true; // Optional fields can be empty
    if (value.endsWith(".")) return false;
    return parseMoneyToNumber(value) !== null;
}

export default function DialogBulkUpdatePartnerAdmin(props: {
    isOpen: boolean;
    onClose: () => void;
    selectedIds: string[];
    onSuccess?: () => void;
}) {
    const { openAlertDialog } = useAlertDialog();

    // Financial Config
    const [stationCom, setStationCom] = useState("");
    const [minimumPayout, setMinimumPayout] = useState("");
    const [maximumPayout, setMaximumPayout] = useState("");
    const [pubRate, setPubRate] = useState("");
    const [customerSupportHotline, setCustomerSupportHotline] = useState("");
    const [stationSupportHotline, setStationSupportHotline] = useState("");
    const [destinationId, setDestinationId] = useState("");

    // Pricing & WorkTime
    const [price, setPrice] = useState("");
    const [insurancePrice, setInsurancePrice] = useState("");
    // Giá lưu theo cỡ túi — để trống = không gửi (giữ nguyên giá đang có của từng station)
    const [feeSmall, setFeeSmall] = useState("");
    const [feeMedium, setFeeMedium] = useState("");
    const [feeLarge, setFeeLarge] = useState("");
    const [fromWork, setFromWork] = useState("");
    const [toWork, setToWork] = useState("");

    const [closureDateRanges, setClosureDateRanges] = useState<ClosureDateRangeRow[]>([]);
    const [permanentClosureEnabled, setPermanentClosureEnabled] = useState(false);
    const [permanentEffectiveFrom, setPermanentEffectiveFrom] = useState("");
    /** When true, bulk update clears closure calendar on every selected station */
    const [clearClosureConfigs, setClearClosureConfigs] = useState(false);

    /** Bulk: whether to update the min-payout on/off flag for selected stations */
    const [updateMinPayoutEnabled, setUpdateMinPayoutEnabled] = useState(false);
    const [minimumPayoutEnabled, setMinimumPayoutEnabled] = useState(false);

    const [updateMinBalanceEnabled, setUpdateMinBalanceEnabled] = useState(false);
    const [minBalanceEnabled, setMinBalanceEnabled] = useState(true);
    const [minBalance, setMinBalance] = useState("");

    // Station Landing toggle
    const [updateImagesEnabled, setUpdateImagesEnabled] = useState(false);
    const [imagesEnabledValue, setImagesEnabledValue] = useState(true);

    // Meet Station Images
    const [updateMeetImages, setUpdateMeetImages] = useState(false);
    const [meetImageFiles, setMeetImageFiles] = useState<File[]>([]);
    const [meetImagePreviews, setMeetImagePreviews] = useState<string[]>([]);
    const [uploadingImages, setUploadingImages] = useState(false);

    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!props.isOpen) return;
        setClosureDateRanges([]);
        setPermanentClosureEnabled(false);
        setPermanentEffectiveFrom("");
        setClearClosureConfigs(false);
        setUpdateImagesEnabled(false);
        setImagesEnabledValue(true);
        setUpdateMeetImages(false);
        setMeetImageFiles([]);
        setMeetImagePreviews([]);
    }, [props.isOpen]);

    const closureBlockError = useMemo(() => {
        if (clearClosureConfigs) return null;
        const wantsClosure =
            closureDateRanges.some((r) => r.from || r.to) || permanentClosureEnabled;
        if (!wantsClosure) return null;
        const built = buildClosureExceptionConfigForApi(
            closureDateRanges,
            permanentClosureEnabled,
            permanentEffectiveFrom,
        );
        return built.ok ? null : built.message;
    }, [
        clearClosureConfigs,
        closureDateRanges,
        permanentClosureEnabled,
        permanentEffectiveFrom,
    ]);

    const errors = useMemo(() => {
        let stationComErr = validateMoneyValue(stationCom);
        let minimumPayoutErr = validateMoneyValue(minimumPayout);
        let maximumPayoutErr = validateMoneyValue(maximumPayout);
        let minBalanceErr = validateMoneyValue(minBalance);
        let pubRateErr = validateMoneyValue(pubRate);
        let priceErr = validateMoneyValue(price);
        let insurancePriceErr = validateMoneyValue(insurancePrice);
        const sizeFeeErr =
            validateMoneyValue(feeSmall) || validateMoneyValue(feeMedium) || validateMoneyValue(feeLarge);

        if (!minimumPayoutErr && !maximumPayoutErr && minimumPayout !== "" && maximumPayout !== "") {
            const min = parseMoneyToNumber(minimumPayout);
            const max = parseMoneyToNumber(maximumPayout);
            if (min !== null && max !== null && min > max) {
                minimumPayoutErr = "Min must be <= Max";
                maximumPayoutErr = "Max must be >= Min";
            }
        }

        return {
            stationCom: stationComErr,
            minimumPayout: minimumPayoutErr,
            maximumPayout: maximumPayoutErr,
            minBalance: minBalanceErr,
            pubRate: pubRateErr,
            price: priceErr,
            insurancePrice: insurancePriceErr,
            sizeFee: sizeFeeErr,
        };
    }, [stationCom, minimumPayout, maximumPayout, minBalance, pubRate, price, insurancePrice, feeSmall, feeMedium, feeLarge]);

    const canSave =
        !isSaving &&
        !closureBlockError &&
        !errors.stationCom &&
        !errors.minimumPayout &&
        !errors.maximumPayout &&
        !errors.minBalance &&
        !errors.pubRate &&
        !errors.price &&
        !errors.sizeFee &&
        isCompleteMoneyValue(stationCom) &&
        isCompleteMoneyValue(minimumPayout) &&
        isCompleteMoneyValue(maximumPayout) &&
        isCompleteMoneyValue(minBalance) &&
        isCompleteMoneyValue(pubRate) &&
        isCompleteMoneyValue(price) &&
            isCompleteMoneyValue(insurancePrice) &&
        isCompleteMoneyValue(feeSmall) &&
        isCompleteMoneyValue(feeMedium) &&
        isCompleteMoneyValue(feeLarge) &&
        (
            clearClosureConfigs ||
            closureDateRanges.some((r) => r.from || r.to) ||
            permanentClosureEnabled ||
            updateMinPayoutEnabled ||
            updateMinBalanceEnabled ||
            stationCom !== "" ||
            minimumPayout !== "" ||
            maximumPayout !== "" ||
            minBalance !== "" ||
            pubRate !== "" ||
            price !== "" ||
            insurancePrice !== "" ||
            feeSmall !== "" ||
            feeMedium !== "" ||
            feeLarge !== "" ||
            fromWork !== "" ||
            toWork !== "" ||
            customerSupportHotline !== "" ||
            stationSupportHotline !== "" ||
            destinationId !== "" ||
            updateImagesEnabled ||
            updateMeetImages
        );

    const handleSave = async () => {
        try {
            if (!canSave) {
                openAlertDialog?.(AlertType.Fail, "Vui lòng nhập dữ liệu hợp lệ.");
                return;
            }
            if (props.selectedIds.length === 0) return;

            setIsSaving(true);

            const data: any = {};
            if (clearClosureConfigs) {
                data.closureExceptionConfig = null;
            } else {
                const wantsClosure =
                    closureDateRanges.some((r) => r.from || r.to) || permanentClosureEnabled;
                if (wantsClosure) {
                    const built = buildClosureExceptionConfigForApi(
                        closureDateRanges,
                        permanentClosureEnabled,
                        permanentEffectiveFrom,
                    );
                    if (!built.ok) {
                        openAlertDialog?.(AlertType.Fail, built.message);
                        return;
                    }
                    data.closureExceptionConfig = built.value;
                }
            }
            if (destinationId !== "") data.destinationId = destinationId;
            if (stationCom !== "") data.stationCom = parseMoneyToNumber(stationCom);
            if (minimumPayout !== "") data.minimumPayout = parseMoneyToNumber(minimumPayout);
            if (maximumPayout !== "") data.maximumPayout = parseMoneyToNumber(maximumPayout);
            if (pubRate !== "") data.pubRate = parseMoneyToNumber(pubRate);
            if (customerSupportHotline !== "") data.generalHotline = customerSupportHotline;
            if (stationSupportHotline !== "") data.stationSupportHotline = stationSupportHotline;
            if (price !== "") data.fee = parseMoneyToNumber(price);
            if (insurancePrice !== "") data.insuranceFee = parseMoneyToNumber(insurancePrice);
            if (feeSmall !== "") data.feeSmall = parseMoneyToNumber(feeSmall);
            if (feeMedium !== "") data.feeMedium = parseMoneyToNumber(feeMedium);
            if (feeLarge !== "") data.feeLarge = parseMoneyToNumber(feeLarge);
            if (updateMinPayoutEnabled) data.minimumPayoutEnabled = minimumPayoutEnabled;
            if (minBalance !== "") data.minBalance = parseMoneyToNumber(minBalance);
            if (updateMinBalanceEnabled) data.minBalanceEnabled = minBalanceEnabled;

            if (updateImagesEnabled) {
                data.imagesEnabled = imagesEnabledValue;
            }
            if (updateMeetImages && meetImageFiles.length > 0) {
                setUploadingImages(true);
                const uploadRes = await uploadAvatarMutiImage(meetImageFiles);
                setUploadingImages(false);
                const items = Array.isArray(uploadRes) ? uploadRes : [];
                const urls = items.map((item: any) => item.url || item.path).filter(Boolean) as string[];
                if (urls.length > 0) {
                    data.meetStationImages = urls;
                    data.images = urls;
                    data.imagesEnabled = true;
                }
            }

            if (fromWork !== "" || toWork !== "") {
                data.workTime = [
                    {
                        hour: {
                            from: fromWork || "00:00",
                            to: toWork || "23:59"
                        }
                    }
                ];
            }

            await PutBulkPartnerAdmin(props.selectedIds, data);

            openAlertDialog?.(AlertType.Success, `Đã cập nhật cấu hình cho ${props.selectedIds.length} stations!`, () => {
                props.onSuccess?.();
                props.onClose();
            });
        } catch (err: any) {
            console.error(err);
            openAlertDialog?.(AlertType.Fail, err?.response?.data?.message || "Đã có lỗi xảy ra!");
        } finally {
            setIsSaving(false);
        }
    };

    const sectionStyle: React.CSSProperties = {
        background: "#F9FAFB",
        border: "1px solid #E5E7EB",
        borderRadius: 10,
        padding: "14px 16px",
        marginBottom: 16,
    };
    const sectionTitleStyle: React.CSSProperties = {
        fontWeight: 700,
        fontSize: 14,
        marginBottom: 12,
        color: "#1F2937",
    };

    return (
        <DialogWrapMedium
            login={true}
            open={props.isOpen}
            onClose={props.onClose}
            title={`Update Selected Stations (${props.selectedIds.length})`}
            actions={
                <>
                    <UserFormServiceButtonClose onClick={props.onClose}>
                        Cancel
                    </UserFormServiceButtonClose>
                    <UserFormServiceButtonCreated
                        disabled={!canSave}
                        onClick={handleSave}>
                        {isSaving ? "Updating..." : "Apply Changes"}
                    </UserFormServiceButtonCreated>
                </>
            }>
            <div style={{ maxHeight: "70vh", overflowY: "auto", paddingRight: "8px" }}>
                <div style={{ marginBottom: 12, fontSize: "13px", color: "#4B5563" }}>
                    <div>
                        <b>Lưu ý:</b> Các thay đổi sẽ được áp dụng cho toàn bộ <b>{props.selectedIds.length}</b> trạm đã chọn.
                        Chỉ những trường có dữ liệu mới bị thay đổi.
                    </div>
                    <div style={{ marginTop: 4 }}>
                        Định dạng số dùng dấu <b>,</b> cho phần ngàn và dấu <b>.</b> cho thập phân. Ví dụ:{" "}
                        <b>25,000.00</b>, <b>1,234,567.89</b>. Tối đa 8 chữ số thập phân.
                    </div>
                    <div style={{ marginTop: 4 }}>
                        Có thể cập nhật <b>đóng cửa theo ngày</b> (khoảng ngày / đóng vĩnh viễn) hoặc gỡ cấu hình đóng cửa theo ngày cho cùng lúc nhiều trạm.
                    </div>
                </div>

                <div style={sectionStyle}>
                    <div style={sectionTitleStyle}>📍 Location</div>
                    <div>
                        <InputOrderAdminTitle>Location</InputOrderAdminTitle>
                        <AdminFilterDesControl
                            filterStaffId={destinationId}
                            doChangeStaffId={setDestinationId}
                        />
                    </div>
                </div>

                <div style={sectionStyle}>
                    <div style={sectionTitleStyle}>💰 Financial Configuration</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                        <div>
                            <InputOrderAdminTitle>STT Com</InputOrderAdminTitle>
                            <FomrmLoginInput
                                placeholder="VD: 50,000.00"
                                value={stationCom}
                                onChange={(e) => setStationCom((prev) => formatMoneyInputDisplay(sanitizeMoneyInput(e.target.value, prev)))}
                            />
                        </div>
                        <div>
                            <InputOrderAdminTitle>Public Rate</InputOrderAdminTitle>
                            <FomrmLoginInput
                                placeholder="VD: 4,75"
                                value={pubRate}
                                onChange={(e) => setPubRate((prev) => formatMoneyInputDisplay(sanitizeMoneyInput(e.target.value, prev)))}
                            />
                        </div>
                        <div>
                            <InputOrderAdminTitle>Customer support</InputOrderAdminTitle>
                            <FomrmLoginInput
                                placeholder="Nhập số điện thoại"
                                value={customerSupportHotline}
                                onChange={(e) => setCustomerSupportHotline(e.target.value)}
                            />
                        </div>
                        <div>
                            <InputOrderAdminTitle>Station support</InputOrderAdminTitle>
                            <FomrmLoginInput
                                placeholder="Nhập số điện thoại"
                                value={stationSupportHotline}
                                onChange={(e) => setStationSupportHotline(e.target.value)}
                            />
                        </div>
                        <div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                                <FormControlLabel
                                    style={{ margin: 0 }}
                                    control={
                                        <Switch
                                            color="primary"
                                            checked={minimumPayoutEnabled}
                                            onChange={(e) => {
                                                setMinimumPayoutEnabled(e.target.checked);
                                                setUpdateMinPayoutEnabled(true);
                                            }}
                                        />
                                    }
                                    label={
                                        <span style={{ fontSize: 14, color: "#374151", fontWeight: 600 }}>
                                            Cho phép rút tiền dưới Min Payout (có phí)
                                        </span>
                                    }
                                />
                                {!updateMinPayoutEnabled && (
                                    <span style={{ fontSize: 12, color: "#9CA3AF", fontStyle: "italic" }}>
                                        (Bấm để cập nhật)
                                    </span>
                                )}
                                {updateMinPayoutEnabled && (
                                    <span
                                        style={{ fontSize: 12, color: "#EF4444", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => {
                                            setUpdateMinPayoutEnabled(false);
                                            setMinimumPayoutEnabled(false);
                                        }}
                                    >
                                        Hủy cập nhật
                                    </span>
                                )}
                            </div>

                            <InputOrderAdminTitle>Min Payout (USD)</InputOrderAdminTitle>
                            <FomrmLoginInput
                                placeholder="VD: 25.000"
                                value={minimumPayout}
                                onChange={(e) => setMinimumPayout((prev) => formatMoneyInputDisplay(sanitizeMoneyInput(e.target.value, prev)))}
                            />
                        </div>
                        <div>
                            <InputOrderAdminTitle>Max Payout (USD)</InputOrderAdminTitle>
                            <FomrmLoginInput
                                placeholder="VD: 1.000.000"
                                value={maximumPayout}
                                onChange={(e) => setMaximumPayout((prev) => formatMoneyInputDisplay(sanitizeMoneyInput(e.target.value, prev)))}
                            />
                        </div>

                        <div style={{ gridColumn: "1 / -1" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                                <FormControlLabel
                                    style={{ margin: 0 }}
                                    control={
                                        <Switch
                                            color="primary"
                                            checked={minBalanceEnabled}
                                            onChange={(e) => {
                                                setMinBalanceEnabled(e.target.checked);
                                                setUpdateMinBalanceEnabled(true);
                                            }}
                                        />
                                    }
                                    label={
                                        <span style={{ fontSize: 14, color: "#374151", fontWeight: 600 }}>
                                            Bật/tắt minimum balance reserve
                                        </span>
                                    }
                                />
                                {!updateMinBalanceEnabled && (
                                    <span style={{ fontSize: 12, color: "#9CA3AF", fontStyle: "italic" }}>
                                        (Bấm switch để cập nhật)
                                    </span>
                                )}
                                {updateMinBalanceEnabled && (
                                    <span
                                        style={{ fontSize: 12, color: "#EF4444", cursor: "pointer", textDecoration: "underline" }}
                                        onClick={() => {
                                            setUpdateMinBalanceEnabled(false);
                                            setMinBalanceEnabled(true);
                                        }}
                                    >
                                        Hủy cập nhật
                                    </span>
                                )}
                            </div>
                            <InputOrderAdminTitle>Min Balance Reserve (USD)</InputOrderAdminTitle>
                            <FomrmLoginInput
                                placeholder="VD: 200.00"
                                value={minBalance}
                                onChange={(e) => setMinBalance((prev) => formatMoneyInputDisplay(sanitizeMoneyInput(e.target.value, prev)))}
                            />
                        </div>
                    </div>
                    {(errors.minimumPayout || errors.maximumPayout || errors.minBalance) && (
                        <ErrorNotiLogin>{errors.minimumPayout || errors.maximumPayout || errors.minBalance}</ErrorNotiLogin>
                    )}
                </div>

                <div style={sectionStyle}>
                    <div style={sectionTitleStyle}>🏷️ Pricing & Services</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                        <div>
                            <InputOrderAdminTitle>Price (Fee)</InputOrderAdminTitle>
                            <FomrmLoginInput
                                placeholder="VD: 50,000.00"
                                value={price}
                                onChange={(e) => setPrice((prev) => formatMoneyInputDisplay(sanitizeMoneyInput(e.target.value, prev)))}
                            />
                        </div>
                        <div>
                            <InputOrderAdminTitle>Insurance Price</InputOrderAdminTitle>
                            <FomrmLoginInput
                                placeholder="VD: 1.500,50"
                                value={insurancePrice}
                                onChange={(e) => setInsurancePrice((prev) => formatMoneyInputDisplay(sanitizeMoneyInput(e.target.value, prev)))}
                            />
                        </div>
                        <div>
                            <InputOrderAdminTitle>Storage fee by bag size (optional)</InputOrderAdminTitle>
                            <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 8 }}>
                                Để trống = giữ nguyên giá đang có. Station chưa từng đặt thì mặc định = Fee × 0.7 / 1.0 / 1.3.
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
                                {(
                                    [
                                        { label: "Small bag", value: feeSmall, set: setFeeSmall },
                                        { label: "Standard suitcase", value: feeMedium, set: setFeeMedium },
                                        { label: "Oversized", value: feeLarge, set: setFeeLarge },
                                    ] as { label: string; value: string; set: React.Dispatch<React.SetStateAction<string>> }[]
                                ).map((col) => (
                                    <div key={col.label}>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>{col.label}</div>
                                        <FomrmLoginInput
                                            placeholder="VD: 2.50"
                                            value={col.value}
                                            onChange={(e) => col.set((prev) => formatMoneyInputDisplay(sanitizeMoneyInput(e.target.value, prev)))}
                                        />
                                    </div>
                                ))}
                            </div>
                            {errors.sizeFee && <ErrorNotiLogin>{errors.sizeFee}</ErrorNotiLogin>}
                        </div>
                    </div>
                </div>

                <div style={sectionStyle}>
                    <div style={sectionTitleStyle}>⏱️ Working Hours</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                        <div>
                            <InputOrderAdminTitle>From Work</InputOrderAdminTitle>
                            <FomrmLoginInput
                                placeholder="HH:mm (e.g. 08:00)"
                                value={fromWork}
                                onChange={(e) => setFromWork(e.target.value)}
                            />
                        </div>
                        <div>
                            <InputOrderAdminTitle>To Work</InputOrderAdminTitle>
                            <FomrmLoginInput
                                placeholder="HH:mm (e.g. 22:00)"
                                value={toWork}
                                onChange={(e) => setToWork(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <div style={sectionStyle}>
                    <div style={sectionTitleStyle}>🚫 Đóng cửa theo ngày (bulk)</div>
                    <FormControlLabel
                        style={{ marginBottom: 12, alignItems: "flex-start" }}
                        control={
                            <Checkbox
                                color="primary"
                                checked={clearClosureConfigs}
                                onChange={(e) => {
                                    setClearClosureConfigs(e.target.checked);
                                }}
                            />
                        }
                        label={
                            <span style={{ fontSize: 13, color: "#374151", lineHeight: 1.4 }}>
                                <b>Gỡ cấu hình đóng cửa theo ngày</b> trên tất cả trạm đã chọn (xóa lịch ngoại lệ, không
                                đổi giờ mở cửa trong ngày).
                            </span>
                        }
                    />
                    <div
                        style={{
                            marginTop: 4,
                            padding: "12px 14px",
                            background: "#FFFBEB",
                            border: "1px solid #FDE68A",
                            borderRadius: 8,
                            opacity: clearClosureConfigs ? 0.55 : 1,
                            pointerEvents: clearClosureConfigs ? "none" : "auto",
                        }}
                    >
                        <div
                            style={{
                                fontWeight: 700,
                                fontSize: 12,
                                color: "#92400E",
                                marginBottom: 8,
                            }}
                        >
                            Đóng cửa theo ngày (cộng với lịch đóng mặc định theo giờ)
                        </div>
                        <div style={{ fontSize: 11, color: "#78350F", marginBottom: 10, lineHeight: 1.45 }}>
                            Ngày trong khoảng hoặc từ ngày đóng vĩnh viễn được coi là đóng cả ngày. Trùng với ngày đã đóng
                            theo giờ làm việc vẫn là đóng. Áp dụng cùng một cấu hình cho mọi trạm đã chọn.
                        </div>

                        {closureDateRanges.map((row, idx) => (
                            <div
                                key={idx}
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr auto",
                                    gap: 8,
                                    alignItems: "end",
                                    marginBottom: 8,
                                }}
                            >
                                <div>
                                    <div
                                        style={{
                                            fontSize: 12,
                                            fontWeight: 600,
                                            color: "#374151",
                                            marginBottom: 4,
                                        }}
                                    >
                                        Từ ngày
                                    </div>
                                    <input
                                        type="date"
                                        value={row.from}
                                        onChange={(e) => {
                                            const next = [...closureDateRanges];
                                            next[idx] = { ...next[idx], from: e.target.value };
                                            setClosureDateRanges(next);
                                        }}
                                        style={{
                                            width: "100%",
                                            boxSizing: "border-box",
                                            padding: "9px 10px",
                                            borderRadius: 6,
                                            border: "1px solid #E5E7EB",
                                            fontSize: 14,
                                        }}
                                    />
                                </div>
                                <div>
                                    <div
                                        style={{
                                            fontSize: 12,
                                            fontWeight: 600,
                                            color: "#374151",
                                            marginBottom: 4,
                                        }}
                                    >
                                        Đến ngày
                                    </div>
                                    <input
                                        type="date"
                                        value={row.to}
                                        onChange={(e) => {
                                            const next = [...closureDateRanges];
                                            next[idx] = { ...next[idx], to: e.target.value };
                                            setClosureDateRanges(next);
                                        }}
                                        style={{
                                            width: "100%",
                                            boxSizing: "border-box",
                                            padding: "9px 10px",
                                            borderRadius: 6,
                                            border: "1px solid #E5E7EB",
                                            fontSize: 14,
                                        }}
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setClosureDateRanges(closureDateRanges.filter((_, i) => i !== idx));
                                    }}
                                    style={{
                                        padding: "8px 10px",
                                        fontSize: 12,
                                        fontWeight: 600,
                                        color: "#B45309",
                                        background: "#FEF3C7",
                                        border: "1px solid #FCD34D",
                                        borderRadius: 6,
                                        cursor: "pointer",
                                    }}
                                >
                                    Xóa
                                </button>
                            </div>
                        ))}

                        <button
                            type="button"
                            onClick={() => setClosureDateRanges((prev) => [...prev, { from: "", to: "" }])}
                            style={{
                                marginBottom: 12,
                                padding: "6px 12px",
                                fontSize: 12,
                                fontWeight: 600,
                                color: "#92400E",
                                background: "#FEF3C7",
                                border: "1px solid #FCD34D",
                                borderRadius: 6,
                                cursor: "pointer",
                            }}
                        >
                            + Thêm khoảng ngày
                        </button>

                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                flexWrap: "wrap",
                                marginBottom: 8,
                            }}
                        >
                            <label
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    cursor: "pointer",
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: "#78350F",
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={permanentClosureEnabled}
                                    onChange={(e) => setPermanentClosureEnabled(e.target.checked)}
                                />
                                Đóng cửa vĩnh viễn (từ một ngày trở đi)
                            </label>
                        </div>
                        {permanentClosureEnabled ? (
                            <div style={{ maxWidth: 280 }}>
                                <div
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 600,
                                        color: "#374151",
                                        marginBottom: 4,
                                    }}
                                >
                                    Bắt đầu từ ngày
                                </div>
                                <input
                                    type="date"
                                    value={permanentEffectiveFrom}
                                    onChange={(e) => setPermanentEffectiveFrom(e.target.value)}
                                    style={{
                                        width: "100%",
                                        boxSizing: "border-box",
                                        padding: "9px 10px",
                                        borderRadius: 6,
                                        border: "1px solid #E5E7EB",
                                        fontSize: 14,
                                    }}
                                />
                            </div>
                        ) : null}
                        {closureBlockError ? (
                            <div style={{ color: "#DC2626", fontSize: 12, marginTop: 8, fontWeight: 600 }}>
                                {closureBlockError}
                            </div>
                        ) : null}
                    </div>
                </div>

                <div style={sectionStyle}>
                    <div style={sectionTitleStyle}>Station Landing</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                        <div>
                            <FormControlLabel
                                style={{ margin: 0 }}
                                control={
                                    <Checkbox
                                        color="primary"
                                        checked={updateImagesEnabled}
                                        onChange={(e) => setUpdateImagesEnabled(e.target.checked)}
                                    />
                                }
                                label={
                                    <span style={{ fontSize: 14, color: "#374151", fontWeight: 600 }}>
                                        Toggle Station Landing Section
                                    </span>
                                }
                            />
                            {updateImagesEnabled && (
                                <div style={{ marginTop: 8, marginLeft: 8 }}>
                                    <FormControlLabel
                                        style={{ margin: 0 }}
                                        control={
                                            <Switch
                                                color="primary"
                                                checked={imagesEnabledValue}
                                                onChange={(e) => setImagesEnabledValue(e.target.checked)}
                                            />
                                        }
                                        label={
                                            <span style={{ fontSize: 13, color: "#374151" }}>
                                                {imagesEnabledValue ? "ON — visible on station page" : "OFF — hidden on station page"}
                                            </span>
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    <div>
                        <FormControlLabel
                            style={{ margin: 0 }}
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={updateMeetImages}
                                    onChange={(e) => {
                                        setUpdateMeetImages(e.target.checked);
                                        if (!e.target.checked) {
                                            setMeetImageFiles([]);
                                            setMeetImagePreviews([]);
                                        }
                                    }}
                                />
                            }
                            label={
                                <span style={{ fontSize: 14, color: "#374151", fontWeight: 600 }}>
                                    Update Meet Station Images
                                </span>
                            }
                        />
                        {updateMeetImages && (
                            <div style={{ marginTop: 10 }}>
                                <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 8 }}>
                                    Select images for "Meet your Station" tab (max 3)
                                </div>
                                <input
                                    type="file"
                                    multiple
                                    accept="image/*"
                                    onChange={(e) => {
                                        const files = Array.from(e.target.files || []).slice(0, 3);
                                        setMeetImageFiles(files);
                                        setMeetImagePreviews(files.map((f) => URL.createObjectURL(f)));
                                    }}
                                    style={{ fontSize: 13 }}
                                />
                                {meetImagePreviews.length > 0 && (
                                    <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                                        {meetImagePreviews.map((url, i) => (
                                            <img
                                                key={i}
                                                src={url}
                                                alt={`Preview ${i + 1}`}
                                                style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 6, border: "1px solid #D1D5DB" }}
                                            />
                                        ))}
                                    </div>
                                )}
                                {uploadingImages && (
                                    <div style={{ marginTop: 8, fontSize: 13, color: "#1565c0" }}>
                                        Uploading images...
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    </div>
                </div>
            </div>
        </DialogWrapMedium>
    );
}
